import { Request } from 'api';
import { AxiosResponse } from 'axios';
import {
    Fp1Device,
    Fp1Shipment,
    Fp1Installation,
    Fp1CustodyEvent,
    Fp1DeviceStatusCounts,
    Fp1BatteryHealthDevice,
    CreateFp1ShipmentParams,
    UpdateFp1DeviceParams,
    Fp1SubscriptionOverrideParams,
    Fp1DebugSession,
    Fp1DebugDownloadResult,
    Fp1DebugSessionExport,
} from './fp1.types';

interface PaginatedResponse<T> {
    data: Array<T>;
    links: any;
    meta: {
        total: number;
        current_page: number;
        per_page: number;
        last_page?: number;
    };
}

interface GetDevicesParams {
    status?: string;
    subscription_status?: string;
    /** Revenue holder: 'athlete' = Transferred (athlete pays own sub), 'org' = org pays. */
    subscription_holder?: string;
    device_type?: string;
    organization_uuid?: string;
    athlete_name?: string;
    shipment_uuid?: string;
    serial_number?: string;
    page?: number;
    per_page?: number;
    /** Only fetch the (expensive) usage telemetry when the Used/Last Used columns are shown. */
    include_usage?: boolean;
    /**
     * Only fetch the live Stripe discount/credit picture when the Management subtab needs
     * it (drives the "Discount / Credit" category + true net MRR). Cached a day server-side.
     */
    include_billing?: boolean;
}

export interface GetFp1HardwareReturnsParams {
    returns_party?: 'all' | 'org' | 'athlete';
    returns_name?: string;
    page?: number;
    per_page?: number;
}

const getFp1Devices = (
    params?: GetDevicesParams,
): Promise<AxiosResponse<PaginatedResponse<Fp1Device>>> => {
    const filterParams: Record<string, any> = {};
    if (params?.status) filterParams.status = params.status;
    if (params?.subscription_status) {
        filterParams.subscription_status = params.subscription_status;
    }
    if (params?.subscription_holder) {
        filterParams.subscription_holder = params.subscription_holder;
    }
    if (params?.device_type) filterParams.device_type = params.device_type;
    if (params?.organization_uuid) filterParams.organization_uuid = params.organization_uuid;
    if (params?.athlete_name) filterParams.athlete_name = params.athlete_name;
    if (params?.shipment_uuid) filterParams.shipment_uuid = params.shipment_uuid;
    if (params?.serial_number) filterParams.serial_number = params.serial_number;
    if (params?.page) filterParams.page = params.page;
    if (params?.per_page) filterParams.per_page = params.per_page;
    if (params?.include_usage) filterParams.include_usage = 1;
    if (params?.include_billing) filterParams.include_billing = 1;
    return Request.get('admin/fp1/devices', { params: filterParams });
};

/**
 * Fetch the entire pod fleet from the Devices endpoint.
 * Used by the read-only Management summary subtab, which needs all pods (not one
 * grid page) to compute fleet-wide rollups. Usage telemetry is intentionally not
 * requested (the summary doesn't need it, and it is expensive on the backend).
 *
 * Fetches page 1 to learn the page count, then requests the remaining pages in
 * parallel so total load time is ~2 round-trips rather than one per page.
 *
 * Results are de-duplicated by pod uuid: the backend paginates by `updated_at`
 * without a stable tiebreaker, so a pod with a shared timestamp can land on two
 * pages. Deduping keeps the fleet-wide rollups from double-counting those pods.
 *
 * Pass `{ includeBilling: true }` to also pull each pod's live Stripe discount/credit
 * (the Management subtab uses this for the "Discount / Credit" category + true net MRR);
 * it is cached a day server-side, so the extra cost is paid only on a cold cache.
 */
const getAllFp1Devices = async (options?: { includeBilling?: boolean }): Promise<Fp1Device[]> => {
    const perPage = 200; // backend caps per_page at 200
    const includeBilling = options?.includeBilling ?? false;
    const first = await getFp1Devices({
        page: 1,
        per_page: perPage,
        include_billing: includeBilling,
    });
    const collected: Fp1Device[] = [...(first.data?.data ?? [])];
    const lastPage = first.data?.meta?.last_page ?? 1;

    if (lastPage > 1) {
        const rest = await Promise.all(
            Array.from({ length: lastPage - 1 }, (_, i) =>
                getFp1Devices({ page: i + 2, per_page: perPage, include_billing: includeBilling }),
            ),
        );
        rest.forEach((res) => collected.push(...(res.data?.data ?? [])));
    }

    // De-dupe by pod uuid: the endpoint paginates by updated_at with no stable
    // tiebreaker, so a pod sharing a timestamp can land on two pages and be
    // double-counted in the fleet-wide rollups.
    const seen = new Set<string>();
    const unique: Fp1Device[] = [];
    for (const d of collected) {
        const key = d.uuid || d.serial_number;
        if (seen.has(key)) continue;
        seen.add(key);
        unique.push(d);
    }
    return unique;
};

const getFp1HardwareReturns = (
    params?: GetFp1HardwareReturnsParams,
): Promise<AxiosResponse<PaginatedResponse<Fp1Device>>> => {
    const p: Record<string, unknown> = {};
    if (params?.returns_party) p.returns_party = params.returns_party;
    if (params?.returns_name?.trim()) p.returns_name = params.returns_name.trim();
    if (params?.page) p.page = params.page;
    if (params?.per_page) p.per_page = params.per_page;
    return Request.get('admin/fp1/devices/returns', { params: p });
};

const getFp1DeviceStatusCounts = (): Promise<AxiosResponse<{ data: Fp1DeviceStatusCounts }>> => {
    return Request.get('admin/fp1/devices/status-counts');
};

interface Fp1DeviceSubscriptionStatusCounts {
    na: number;
    comp: number;
    invoiced: number;
    /** Pods transferred to an athlete who pays their own (active) subscription. */
    active_athlete: number;
    /** Org pods where the org's billed seats fully cover its responsible pod count. */
    active_org: number;
    /** Org pods where the org has fewer billed seats than responsible pods. */
    partial_org: number;
    past_due: number;
    no_sub: number;
    /** Total seat shortfall across partial orgs (retained for backward-compat). */
    excess_pods: number;
}

const getFp1DeviceSubscriptionStatusCounts = (): Promise<
    AxiosResponse<{ data: Fp1DeviceSubscriptionStatusCounts }>
> => {
    return Request.get('admin/fp1/devices/subscription-status-counts');
};

const getFp1Device = (uuid: string): Promise<AxiosResponse<{ data: Fp1Device }>> => {
    return Request.get(`admin/fp1/devices/${uuid}`);
};

const updateFp1Device = (
    uuid: string,
    data: UpdateFp1DeviceParams,
): Promise<AxiosResponse<{ data: Fp1Device }>> => {
    return Request.put(`admin/fp1/devices/${uuid}`, data);
};

const addFp1DeviceNote = (
    uuid: string,
    notes: string,
): Promise<AxiosResponse<{ data: Fp1Device }>> => {
    return Request.post(`admin/fp1/devices/${uuid}/note`, { notes });
};

/**
 * Manually set a pod's subscription status to Comp or Invoiced, or remove it entirely
 * (`type: 'none'`). Every variant records a custody event.
 */
const setFp1DeviceSubscriptionOverride = (
    uuid: string,
    data: Fp1SubscriptionOverrideParams,
): Promise<AxiosResponse<{ data: Fp1Device }>> => {
    return Request.post(`admin/fp1/devices/${uuid}/subscription-override`, data);
};

const getFp1DeviceCustodyHistory = (
    uuid: string,
): Promise<AxiosResponse<Fp1CustodyEvent[] | { data: Fp1CustodyEvent[] }>> => {
    return Request.get(`admin/fp1/devices/${uuid}/custody`);
};

export interface GetReturnHistoryParams {
    serial_number?: string;
    org_name?: string;
    athlete_name?: string;
    page?: number;
    per_page?: number;
}

const getReturnHistory = (
    params?: GetReturnHistoryParams,
): Promise<AxiosResponse<PaginatedResponse<Fp1Device>>> => {
    const p: Record<string, any> = {};
    if (params?.serial_number) p.serial_number = params.serial_number;
    if (params?.org_name) p.org_name = params.org_name;
    if (params?.athlete_name) p.athlete_name = params.athlete_name;
    if (params?.page) p.page = params.page;
    if (params?.per_page) p.per_page = params.per_page;
    return Request.get('admin/fp1/devices/return-history', { params: p });
};

/**
 * A pod handed to a designated partner org (as opposed to returned to Fiyrpod/AMT's own
 * warehouse). Sourced from the frozen `returned_to_org` custody event: `destination_*`
 * is the receiving org; `previous_*` is who the pod was assigned to before the return.
 */
export interface Fp1ReturnedToOrgRow {
    uuid: string;
    serial_number: string | null;
    destination_org_uuid: string | null;
    destination_org_name: string | null;
    previous_org_uuid: string | null;
    previous_org_name: string | null;
    previous_athlete_uuid: string | null;
    previous_athlete_name: string | null;
    previous_athlete_email: string | null;
    subscription_id: string | null;
    subscription_name: string | null;
    returned_at: string | null;
    processed_by: string | null;
}

export interface GetReturnHistoryToOrgParams {
    /** Pod serial number (partial match). */
    serial_number?: string;
    /** Receiving/destination organization name (partial match). */
    org_name?: string;
    /** Previous custodian athlete name or email (partial match). */
    athlete_name?: string;
    page?: number;
    per_page?: number;
}

const getReturnHistoryToOrg = (
    params?: GetReturnHistoryToOrgParams,
): Promise<AxiosResponse<PaginatedResponse<Fp1ReturnedToOrgRow>>> => {
    const p: Record<string, any> = {};
    if (params?.serial_number) p.serial_number = params.serial_number;
    if (params?.org_name) p.org_name = params.org_name;
    if (params?.athlete_name) p.athlete_name = params.athlete_name;
    if (params?.page) p.page = params.page;
    if (params?.per_page) p.per_page = params.per_page;
    return Request.get('admin/fp1/devices/return-history-to-org', { params: p });
};

interface GetShipmentsParams {
    supplier_name?: string;
    po_number?: string;
    page?: number;
    per_page?: number;
}

const getFp1Shipments = (
    params?: GetShipmentsParams,
): Promise<AxiosResponse<PaginatedResponse<Fp1Shipment>>> => {
    const filterParams: Record<string, any> = {};
    if (params?.supplier_name) filterParams.supplier_name = params.supplier_name;
    if (params?.po_number) filterParams.po_number = params.po_number;
    if (params?.page) filterParams.page = params.page;
    if (params?.per_page) filterParams.per_page = params.per_page;
    return Request.get('admin/fp1/shipments', { params: filterParams });
};

const getFp1Shipment = (uuid: string): Promise<AxiosResponse<{ data: Fp1Shipment }>> => {
    return Request.get(`admin/fp1/shipments/${uuid}`);
};

const createFp1Shipment = (
    data: CreateFp1ShipmentParams,
): Promise<AxiosResponse<{ data: Fp1Shipment }>> => {
    return Request.post('admin/fp1/shipments', data);
};

interface GetInstallationsParams {
    status?: string;
    organization_uuid?: string;
    page?: number;
    per_page?: number;
}

export interface Fp1MarketplaceOrderSettings {
    physical_product_uuids_requiring_pod_serial: string[];
}

const getFp1MarketplaceOrderSettings = (): Promise<
    AxiosResponse<{ data: Fp1MarketplaceOrderSettings }>
> => Request.get('admin/fp1/marketplace-order-settings');

const getFp1Installations = (
    params?: GetInstallationsParams,
): Promise<AxiosResponse<PaginatedResponse<Fp1Installation>>> => {
    const filterParams: Record<string, any> = {};
    if (params?.status) filterParams.status = params.status;
    if (params?.organization_uuid) filterParams.organization_uuid = params.organization_uuid;
    if (params?.page) filterParams.page = params.page;
    if (params?.per_page) filterParams.per_page = params.per_page;
    return Request.get('admin/fp1/installations', { params: filterParams });
};

export interface GetBatteryHealthParams {
    serial_number?: string;
    health?: 'good' | 'fair' | 'poor' | 'no_data';
    page?: number;
    per_page?: number;
    sort_by?:
        | 'drain_per_hour'
        | 'upload_count'
        | 'last_activity_date'
        | 'last_deployed_at'
        | 'organization'
        | 'athlete';
    sort_dir?: 'asc' | 'desc';
}

const getFp1BatteryHealth = (
    params?: GetBatteryHealthParams,
): Promise<
    AxiosResponse<{
        data: Fp1BatteryHealthDevice[];
        meta: { total: number; current_page: number; per_page: number; last_page: number };
        links: any;
    }>
> => {
    const p: Record<string, any> = {};
    if (params?.serial_number) p.serial_number = params.serial_number;
    if (params?.health) p.health = params.health;
    if (params?.page) p.page = params.page;
    if (params?.per_page) p.per_page = params.per_page;
    if (params?.sort_by) p.sort_by = params.sort_by;
    if (params?.sort_dir) p.sort_dir = params.sort_dir;
    return Request.get('admin/fp1/devices/battery-health', { params: p });
};

export interface GetFp1DebugSessionsParams {
    org_name?: string;
    athlete_name?: string;
    serial_number?: string;
}

const getFp1DebugSessions = (
    params: GetFp1DebugSessionsParams,
): Promise<AxiosResponse<{ data: Fp1DebugSession[] }>> => {
    const p: Record<string, string> = {};
    if (params.org_name?.trim()) p.org_name = params.org_name.trim();
    if (params.athlete_name?.trim()) p.athlete_name = params.athlete_name.trim();
    if (params.serial_number?.trim()) p.serial_number = params.serial_number.trim();
    return Request.get('admin/fp1/debugging/sessions', { params: p });
};

const getFp1DebugDownload = (
    uploadUuid: string,
): Promise<AxiosResponse<{ data: Fp1DebugDownloadResult }>> => {
    return Request.get(`admin/fp1/debugging/uploads/${uploadUuid}/download`);
};

// Whole-session debug bundle (zip). Built on the queue — start it, then poll the status.
const startFp1DebugSessionExport = (
    uploadUuids: string[],
): Promise<AxiosResponse<{ data: Fp1DebugSessionExport }>> => {
    return Request.post('admin/fp1/debugging/session-exports', { upload_uuids: uploadUuids });
};

const getFp1DebugSessionExport = (
    exportId: string,
): Promise<AxiosResponse<{ data: Fp1DebugSessionExport }>> => {
    return Request.get(`admin/fp1/debugging/session-exports/${exportId}`);
};

export interface Fp1DebugSuggestion {
    label: string;
    value: string;
}

const getFp1DebugSuggestions = (
    type: 'org' | 'athlete' | 'serial',
    q: string,
): Promise<AxiosResponse<{ data: Fp1DebugSuggestion[] }>> => {
    return Request.get('admin/fp1/debugging/suggestions', { params: { type, q } });
};

export {
    getFp1Devices,
    getAllFp1Devices,
    getFp1HardwareReturns,
    getFp1DeviceStatusCounts,
    getFp1DeviceSubscriptionStatusCounts,
    getFp1Device,
    updateFp1Device,
    addFp1DeviceNote,
    setFp1DeviceSubscriptionOverride,
    getFp1DeviceCustodyHistory,
    getReturnHistory,
    getReturnHistoryToOrg,
    getFp1Shipments,
    getFp1Shipment,
    createFp1Shipment,
    getFp1Installations,
    getFp1MarketplaceOrderSettings,
    getFp1BatteryHealth,
    getFp1DebugSessions,
    getFp1DebugDownload,
    getFp1DebugSuggestions,
    startFp1DebugSessionExport,
    getFp1DebugSessionExport,
};

export type { GetDevicesParams, GetShipmentsParams, GetInstallationsParams };
